import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import agentsService, { isQuotaStreamError, streamSendMessage } from '@/services/agents.service';
import {
  Agent,
  Citation,
  ConversationDetail,
  ConversationListItem,
  Message,
  StreamPhase,
} from '@/types/agents';
import { quotaErrorMessage } from '@/utils/quotaErrorToast';
import ChatMessage from '@/components/agents/ChatMessage';
import ChatInput from '@/components/agents/ChatInput';
import ThinkingIndicator from '@/components/agents/ThinkingIndicator';
import ConversationSidebar from '@/components/agents/ConversationSidebar';
import LoadingSpinner from '@/components/LoadingSpinner';
import QuotaIndicator from '@/components/QuotaIndicator';
import { emitQuotaRefresh } from '@/utils/quotaRefreshBus';

const AgentChatPage: FC = () => {
  const { id: agentId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [agent, setAgent] = useState<Agent | null>(null);
  const [conversations, setConversations] = useState<ConversationListItem[]>([]);
  const [activeConv, setActiveConv] = useState<ConversationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [streamingPhase, setStreamingPhase] = useState<StreamPhase | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isConvSidebarOpen, setIsConvSidebarOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [pinned, setPinned] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const messages = useMemo<Message[]>(() => activeConv?.messages ?? [], [activeConv]);

  const loadConversations = useCallback(async () => {
    if (!agentId) return [];
    const list = await agentsService.listConversations(agentId);
    setConversations(list);
    return list;
  }, [agentId]);

  const abortInflight = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  }, []);

  const openConversation = useCallback(
    async (convId: string) => {
      abortInflight();
      const detail = await agentsService.getConversation(convId);
      setActiveConv(detail);
    },
    [abortInflight],
  );

  const createConversation = useCallback(async () => {
    if (!agentId) return;
    abortInflight();
    const created = await agentsService.createConversation(agentId);
    setActiveConv(created);
    setConversations((prev) => [
      {
        id: created.id,
        title: created.title,
        last_message_at: created.last_message_at,
        archived: created.archived,
        created_at: created.created_at,
        updated_at: created.updated_at,
      },
      ...prev,
    ]);
  }, [agentId, abortInflight]);

  // J05: fixar/desafixar este agente no dashboard (otimista, reverte no erro).
  const handleTogglePin = useCallback(async () => {
    if (!agentId) return;
    const next = !pinned;
    setPinned(next);
    try {
      if (next) await agentsService.pin(agentId);
      else await agentsService.unpin(agentId);
    } catch {
      setPinned(!next);
    }
  }, [agentId, pinned]);

  // Boot: carrega agent + conversas e abre a mais recente ou cria uma nova
  useEffect(() => {
    let cancelled = false;
    async function boot() {
      if (!agentId) return;
      try {
        setLoading(true);
        const [agentData, convList] = await Promise.all([
          agentsService.getAgent(agentId),
          agentsService.listConversations(agentId),
        ]);
        if (cancelled) return;
        setAgent(agentData);
        setConversations(convList);
        // Estado de "fixado" — best-effort, não bloqueia o boot (J05).
        agentsService
          .listPinned()
          .then((pins) => {
            if (!cancelled) setPinned(pins.some((p) => p.agent_id === agentId));
          })
          .catch(() => {
            /* sem pins — botão fica como "fixar" */
          });
        // Sugestões do empty-state — best-effort, não bloqueia o boot.
        agentsService
          .getSuggestions(agentId)
          .then((s) => {
            if (!cancelled) setSuggestions(s);
          })
          .catch(() => {
            /* sem sugestões — empty-state fica sem os botões */
          });
        if (convList.length > 0) {
          const detail = await agentsService.getConversation(convList[0].id);
          if (!cancelled) setActiveConv(detail);
        } else {
          const created = await agentsService.createConversation(agentId);
          if (!cancelled) {
            setActiveConv(created);
            setConversations([
              {
                id: created.id,
                title: created.title,
                last_message_at: created.last_message_at,
                archived: created.archived,
                created_at: created.created_at,
                updated_at: created.updated_at,
              },
            ]);
          }
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) setError('Não foi possível carregar o agente.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    boot();
    return () => {
      cancelled = true;
      abortInflight();
    };
  }, [agentId, abortInflight]);

  // Auto-scroll ao final: rola por mudança de quantidade OU pelo crescimento
  // do conteúdo da última mensagem (streaming token-a-token).
  const lastContentLength = messages[messages.length - 1]?.content.length ?? 0;
  useEffect(() => {
    if (typeof messagesEndRef.current?.scrollIntoView === 'function') {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length, sending, lastContentLength]);

  async function handleSend(content: string) {
    if (!activeConv || !agentId || !agent) return;
    const convId = activeConv.id;
    const optimisticUserId = `optimistic-user-${Date.now()}`;
    const optimisticUser: Message = {
      id: optimisticUserId,
      role: 'USER',
      content,
      tokens_in: 0,
      tokens_out: 0,
      model_used: '',
      metadata: {},
      created_at: new Date().toISOString(),
    };

    setSending(true);
    setError(null);
    setActiveConv((prev) =>
      prev ? { ...prev, messages: [...prev.messages, optimisticUser] } : prev,
    );

    // Caminho SQL: continua síncrono — backend não suporta streaming pra SQL ainda.
    if (agent.type === 'SQL') {
      try {
        const resp = await agentsService.sendMessage(convId, content);
        setActiveConv((prev) => {
          if (!prev) return prev;
          const withoutOptimistic = prev.messages.filter((m) => m.id !== optimisticUserId);
          return {
            ...prev,
            messages: [...withoutOptimistic, resp.user_message, resp.assistant_message],
          };
        });
        await loadConversations();
        // P14: refresca o QuotaIndicator (SQL também consome tokens)
        emitQuotaRefresh();
      } catch (err) {
        console.error(err);
        if (isQuotaStreamError(err)) {
          setError(quotaErrorMessage(err.data));
        } else {
          setError('Erro ao enviar mensagem. Tente de novo.');
        }
        setActiveConv((prev) =>
          prev
            ? { ...prev, messages: prev.messages.filter((m) => m.id !== optimisticUserId) }
            : prev,
        );
      } finally {
        setSending(false);
      }
      return;
    }

    // Caminho RAG: streaming SSE token-a-token.
    const streamingAssistantId = `streaming-assistant-${Date.now()}`;
    const streamingAssistant: Message = {
      id: streamingAssistantId,
      role: 'ASSISTANT',
      content: '',
      tokens_in: 0,
      tokens_out: 0,
      model_used: '',
      metadata: {},
      created_at: new Date().toISOString(),
    };
    setActiveConv((prev) =>
      prev ? { ...prev, messages: [...prev.messages, streamingAssistant] } : prev,
    );

    abortInflight();
    const controller = new AbortController();
    abortRef.current = controller;
    let serverErrorMsg: string | null = null;
    let lastCitations: Citation[] = [];

    try {
      await streamSendMessage(
        convId,
        content,
        {
          onStatus: (phase) => setStreamingPhase(phase),
          onToken: (chunk) => {
            setStreamingPhase(null);
            setActiveConv((prev) => {
              if (!prev) return prev;
              return {
                ...prev,
                messages: prev.messages.map((m) =>
                  m.id === streamingAssistantId ? { ...m, content: m.content + chunk } : m,
                ),
              };
            });
          },
          onDone: (payload) => {
            lastCitations = payload.citations;
            setActiveConv((prev) => {
              if (!prev) return prev;
              return {
                ...prev,
                messages: prev.messages.map((m) => {
                  if (m.id === optimisticUserId) {
                    return { ...m, id: payload.user_message_id };
                  }
                  if (m.id === streamingAssistantId) {
                    return {
                      ...m,
                      id: payload.message_id,
                      tokens_in: payload.tokens.in,
                      tokens_out: payload.tokens.out,
                      model_used: payload.model ?? m.model_used,
                      model_info: payload.model_info ?? m.model_info,
                      metadata: { ...m.metadata, citations: payload.citations },
                    };
                  }
                  return m;
                }),
              };
            });
          },
          onError: (payload) => {
            serverErrorMsg = payload.message || 'Erro ao consultar a base de conhecimento.';
          },
        },
        controller.signal,
      );
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') {
        // user cancelou (troca de conversa/agente) — silencioso
        return;
      }
      console.error(err);
      if (isQuotaStreamError(err)) {
        serverErrorMsg = quotaErrorMessage(err.data);
      } else if (!serverErrorMsg) {
        serverErrorMsg = 'Erro ao enviar mensagem. Tente de novo.';
      }
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
      setStreamingPhase(null);
      setSending(false);
    }

    if (serverErrorMsg) {
      setError(serverErrorMsg);
      // remove o balão de stream (mantém a pergunta, mais útil)
      setActiveConv((prev) =>
        prev
          ? { ...prev, messages: prev.messages.filter((m) => m.id !== streamingAssistantId) }
          : prev,
      );
      return;
    }

    // sucesso — refresca a sidebar de conversas (last_message_at)
    void loadConversations();
    // P14: refresca o QuotaIndicator (consumiu tokens dessa resposta)
    emitQuotaRefresh();
    // garante que mesmo sem onDone (caso edge) as citations não sumam — defensivo
    if (lastCitations.length > 0) {
      setActiveConv((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          messages: prev.messages.map((m) =>
            m.id.startsWith('streaming-assistant-')
              ? { ...m, metadata: { ...m.metadata, citations: lastCitations } }
              : m,
          ),
        };
      });
    }
  }

  if (loading) {
    return (
      <div className="flex h-[calc(100vh-4rem)] lg:h-screen items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error && (!agent || !activeConv)) {
    return (
      <div className="p-10 text-center">
        <p className="text-red-400">{error || 'Agente não encontrado.'}</p>
        <button
          type="button"
          onClick={() => navigate('/agentes')}
          className="mt-4 text-accent underline"
        >
          Voltar ao Hub
        </button>
      </div>
    );
  }

  if (!agent || !activeConv) {
    return (
      <div className="p-10 text-center">
        <p className="text-red-400">Agente não encontrado.</p>
        <button
          type="button"
          onClick={() => navigate('/agentes')}
          className="mt-4 text-accent underline"
        >
          Voltar ao Hub
        </button>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-4rem)] lg:h-screen overflow-hidden" data-testid="agent-chat-page">
      <section className="flex-1 flex flex-col min-w-0">
        <header className="border-b border-surface-border bg-surface-primary p-4 flex items-center gap-3">
          <span className="material-symbols-outlined text-accent text-3xl">{agent.avatar || 'psychology'}</span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-lg font-semibold text-content-primary truncate">{agent.name}</h1>
              {/* P13/G5: avisa o user que admin pode ler conversas em SECTOR/GLOBAL. */}
              {agent.scope !== 'PERSONAL' && (
                <span
                  data-testid="admin-visibility-badge"
                  title="Admin pode revisar essa conversa pra fins de qualidade e auditoria."
                  className="inline-flex items-center gap-1 text-2xs px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-200"
                >
                  <span aria-hidden>🔓</span>
                  Admin pode ler
                </span>
              )}
            </div>
            <p className="text-xs text-content-tertiary truncate">{agent.description}</p>
          </div>
          <button
            type="button"
            data-testid="pin-toggle"
            aria-label={pinned ? 'Desafixar do dashboard' : 'Fixar no dashboard'}
            aria-pressed={pinned}
            title={pinned ? 'Desafixar do dashboard' : 'Fixar no dashboard'}
            onClick={handleTogglePin}
            className={`shrink-0 grid place-items-center w-9 h-9 rounded-lg transition hover:bg-surface-hover ${
              pinned ? 'text-accent' : 'text-content-tertiary hover:text-accent'
            }`}
          >
            <span
              className="material-symbols-outlined"
              style={pinned ? { fontVariationSettings: "'FILL' 1" } : undefined}
            >
              push_pin
            </span>
          </button>
          <QuotaIndicator />
          {/* Abre o drawer de conversas — só no mobile */}
          <button
            type="button"
            onClick={() => setIsConvSidebarOpen(true)}
            className="lg:hidden p-2 hover:bg-surface-hover rounded-lg shrink-0"
            aria-label="Abrir conversas"
          >
            <span className="material-symbols-outlined">forum</span>
          </button>
        </header>

        <div className="flex-1 min-h-0 overflow-y-auto p-4 md:p-6 space-y-4 bg-surface-primary" data-testid="messages-container">
          {messages.length === 0 && (
            <div className="max-w-2xl mx-auto py-12">
              <div className="text-center mb-6">
                <span className="material-symbols-outlined text-5xl text-accent mb-3 block">
                  {agent.avatar || 'psychology'}
                </span>
                <h2 className="text-xl font-bold text-content-primary mb-2">{agent.name}</h2>
                <p className="text-sm text-content-tertiary">
                  {agent.type === 'SQL'
                    ? 'Faça perguntas em português normal sobre faturamento, vendas, representantes e produtos.'
                    : 'Faça perguntas sobre os documentos deste agente.'}
                </p>
              </div>

              {suggestions.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-wider text-content-muted mb-3 font-bold">
                    💡 Exemplos pra começar
                  </p>
                  <div className="space-y-2">
                    {suggestions.map((suggestion) => (
                      <button
                        key={suggestion}
                        type="button"
                        onClick={() => handleSend(suggestion)}
                        disabled={sending}
                        className="w-full text-left text-sm px-4 py-3 rounded-xl bg-surface-secondary hover:bg-surface-hover border border-surface-border transition-colors disabled:opacity-50"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          {messages.map((m) => (
            <ChatMessage key={m.id} message={m} avatar={agent.avatar} />
          ))}
          {sending && streamingPhase && (
            <ThinkingIndicator type={agent.type} phase={streamingPhase} />
          )}
          {sending && !streamingPhase && agent.type === 'SQL' && (
            <ThinkingIndicator type={agent.type} />
          )}
          {error && (
            <div data-testid="chat-error" className="mx-auto max-w-2xl text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
              {error}
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <ChatInput
          disabled={sending}
          onSend={handleSend}
          placeholder={
            agent.type === 'SQL'
              ? 'Pergunte algo sobre faturamento, representantes, produtos…'
              : 'Pergunte algo sobre os documentos deste agente…'
          }
        />
      </section>

      <ConversationSidebar
        conversations={conversations}
        activeId={activeConv.id}
        onSelect={(id) => {
          void openConversation(id);
          setIsConvSidebarOpen(false);
        }}
        onNew={() => {
          void createConversation();
          setIsConvSidebarOpen(false);
        }}
        isMobileOpen={isConvSidebarOpen}
        onMobileClose={() => setIsConvSidebarOpen(false)}
      />
    </div>
  );
};

export default AgentChatPage;
